import { Divider, TextField } from "@mui/material";
import React, { ChangeEvent, useCallback, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import useToast from "@/contexts/toast-context";
import { getAccessToken } from "@/helpers/auth-helpers";
import { usePlatformId } from "@/hooks/requests/platform/use-platform-id";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import useLoading from "@/hooks/use-loading";
import { WhatsappPhoneConnectionMessages } from "@/messages/platforms/whatsappConnection/whatsappPhoneConnectionMessages";
import customerPlatformService from "@/services/customer-platform.service";
import { PlatformsName } from "@/types/platform.types";

interface Props {
  messages: WhatsappPhoneConnectionMessages;
  wabaId: number;
  phoneNumberId: number;
  onPhoneConnection: () => void;
}

export function PhoneConnection({ messages, wabaId, phoneNumberId, onPhoneConnection }: Props) {
  const [pin, setPin] = useState<null | string>();
  const [error, setError] = useState<string>("");
  const { getPlatformByIdentifier } = usePlatformId();
  const { id: platformId } = getPlatformByIdentifier(PlatformsName.WHATSAPP)!;
  const customerId = useGetCustomerId()!;
  const accessToken = getAccessToken()!;
  const { showToast } = useToast();
  const { isLoading, startLoading, endLoading } = useLoading();

  const handleChangePin = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      if (/^\d*$/.test(value) || value === "") {
        setPin(value);
        setError("");
      } else {
        setError(messages.whatsappPhoneConnectionPinError);
      }
    },
    [messages.whatsappPhoneConnectionPinError]
  );

  const handleSubscribeToWABA = useCallback(async () => {
    if (pin?.length !== 6) {
      setError(messages.whatsappPhoneConnectionPinError);
      return;
    }
    try {
      startLoading();
      const customerExternalId = wabaId.toString();
      const platformProfileId = phoneNumberId.toString();
      const recoveryCode = pin?.toString();
      const active = true;

      await customerPlatformService.whatsappSubscription(
        {
          customerExternalId,
          platformProfileId,
          platformId,
          recoveryCode,
          Active: active, // Usar nome correto da API
          displayName: "",
          displayNumber: "",
        },
        accessToken,
        customerId
      );
      onPhoneConnection();
    } catch (error) {
      const text = messages.whatsappPhoneConnectionSomethingWentWrong;
      showToast({ duration: 10000, text, type: "error" });
    } finally {
      endLoading();
    }
  }, [
    accessToken,
    customerId,
    endLoading,
    messages.whatsappPhoneConnectionPinError,
    messages.whatsappPhoneConnectionSomethingWentWrong,
    onPhoneConnection,
    phoneNumberId,
    pin,
    platformId,
    showToast,
    startLoading,
    wabaId,
  ]);

  return (
    <MDBox>
      <MDBox width="82%" textAlign="left" mx="auto" my={4}>
        <MDBox mb={1}>
          <MDTypography variant="h5" fontWeight="regular">
            {messages.whatsappPhoneConnectionText1Part1}
          </MDTypography>
        </MDBox>

        <MDTypography variant="body2" color="text">
          {messages.whatsappPhoneConnectionText1Part2}
        </MDTypography>

        <Divider />

        <MDBox display={"flex"} mx="auto" gap={3} flexWrap={"wrap"}>
          <TextField
            variant="standard"
            name="code"
            label={messages.whatsappPhoneConnectionInformPin}
            value={pin}
            onChange={handleChangePin}
            error={!!error}
            helperText={error}
            required
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <MDButton
            variant="gradient"
            color="dark"
            size="small"
            isLoading={isLoading}
            onClick={handleSubscribeToWABA}
            disabled={isLoading}
          >
            {messages.whatsappPhoneConnectionActivateNumber}
          </MDButton>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
